import { FunctionComponent, useEffect, useState } from 'react'
import { WButton, WDropdown } from '@alice-health/wonderland-react'

import { useTargetOS } from '@/global/hooks'
import type { OperationSystem } from '@/global/types'

interface ShareLinkButtonProps {
  /**
   * Resolve a URL absoluta que reabre este detalhe, chamada so no momento do
   * clique. A URL selada e escrita de forma assincrona (Web Crypto); resolver
   * cedo demais (no render, numa prop estatica) pode capturar o endereco de
   * antes do selamento terminar.
   */
  getUrl: () => string | undefined
  /** Nome do prestador. Vira o titulo do cabecalho da folha nativa. */
  title?: string
}

const COPY_VALUE = 'copy'
const FEEDBACK_TIMEOUT_MS = 2000

const MOBILE_OS: OperationSystem[] = ['iOS', 'iPadOS', 'Android']

/*
 * `value` fica num sentinela que nenhum item usa: o menu do handoff (node
 * 69:90262) e uma lista de acoes, entao nenhuma linha pode ficar marcada como
 * selecionada depois do clique.
 */
const NO_SELECTION = ''

const ShareLinkButton: FunctionComponent<ShareLinkButtonProps> = ({
  getUrl,
  title
}) => {
  const [copied, setCopied] = useState(false)

  /*
   * `useTargetOS` responde `unknown` ate o efeito de montagem rodar, entao o
   * render do servidor e o primeiro render do cliente caem os dois no dropdown.
   */
  const targetOS = useTargetOS()

  const canShareNatively =
    MOBILE_OS.includes(targetOS) && typeof navigator.share === 'function'

  useEffect(() => {
    if (!copied) return

    const timeout = setTimeout(() => setCopied(false), FEEDBACK_TIMEOUT_MS)

    return () => clearTimeout(timeout)
  }, [copied])

  const copyLink = async () => {
    const url = getUrl()
    if (!url) return

    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      /*
       * `writeText` recusa sem contexto seguro ou sem permissao de clipboard.
       * Nao ha o que fazer alem de nao confirmar a copia.
       */
      setCopied(false)
    }
  }

  const shareNatively = async () => {
    const url = getUrl()
    if (!url) return

    try {
      await navigator.share({ title, url })
    } catch {
      /*
       * Fechar a folha sem escolher destino rejeita com `AbortError`. Nao ha
       * feedback a dar nem no cancelamento nem numa falha real do share.
       */
    }
  }

  const trigger = (
    <WButton
      data-testid='share-link-trigger'
      aria-label='Compartilhar'
      iconButton
      icon='icShareiOS'
      variant='white'
      size='medium'
      onClick={canShareNatively ? shareNatively : undefined}
    />
  )

  if (canShareNatively) return trigger

  return (
    <WDropdown
      data-testid='share-link-dropdown'
      items={[
        {
          label: copied ? 'Link copiado' : 'Copiar link',
          value: COPY_VALUE,
          icon: copied ? 'icSuccess' : 'icCopy'
        }
      ]}
      value={NO_SELECTION}
      position='bottom-end'
      onWChange={copyLink}
    >
      {trigger}
    </WDropdown>
  )
}

export type { ShareLinkButtonProps }

export { ShareLinkButton }
